import { ENV } from "@/config/environment";
import { hasModuleLevelFromPermissions, type RbacLevel } from "@/config/rbac";
import type {
  ApiResponse,
  MfaLoginChallenge,
  TokenResponse,
  UserProfile,
} from "@/types/api";
import {
  bufToB64url,
  requestOptionsFromJSON,
  type RequestOptionsJSON,
} from "@/services/webauthnCodec";

const STORAGE_KEY = "aequia.auth";
const REFRESH_GRACE_MS = 30_000;

interface PersistedAuth {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  tenantId: string | null;
  user: UserProfile | null;
}

export interface AuthenticatedFetchOptions {
  omitTenantHeader?: boolean;
  tenantIdOverride?: string;
}

function decodeJwtTenantId(token: string): string | null | undefined {
  const encodedPayload = token.split(".")[1];
  if (!encodedPayload) return undefined;
  try {
    const base64 = encodedPayload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, "=");
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes)) as {
      tenantId?: unknown;
    };
    if (payload.tenantId === null) return null;
    return typeof payload.tenantId === "string" ? payload.tenantId : undefined;
  } catch {
    return undefined;
  }
}

function normalizePersistedAuth(auth: PersistedAuth): PersistedAuth {
  const tokenTenantId = decodeJwtTenantId(auth.accessToken);
  if (tokenTenantId === undefined || auth.tenantId === tokenTenantId) {
    return auth;
  }
  return {
    ...auth,
    tenantId: tokenTenantId,
    user: auth.user ? { ...auth.user, tenantId: tokenTenantId } : auth.user,
  };
}

function loadFromStorage(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAuth;
    const normalized = normalizePersistedAuth(parsed);
    if (normalized !== parsed) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    }
    return normalized;
  } catch {
    return null;
  }
}

let state: PersistedAuth | null = loadFromStorage();
let refreshPromise: Promise<void> | null = null;
let currentTenantId: string | null = state?.tenantId ?? null;

function persist() {
  if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  else localStorage.removeItem(STORAGE_KEY);
}

export class AuthApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public status?: number,
  ) {
    super(message);
    this.name = "AuthApiError";
  }
}

export class MfaRequiredError extends Error {
  constructor(public challenge: MfaLoginChallenge) {
    super("MFA required");
    this.name = "MfaRequiredError";
  }
}

export function getCurrentTenantId(): string | null {
  return currentTenantId;
}

export async function getCurrentAccessToken(): Promise<string | null> {
  if (shouldRefresh()) {
    refreshPromise ??= doRefresh().finally(() => {
      refreshPromise = null;
    });
    try {
      await refreshPromise;
    } catch {
      window.location.assign("/login");
      throw new Error("Session expired");
    }
  }
  return state?.accessToken ?? null;
}

export function setCurrentTenantId(tenantId: string | null) {
  currentTenantId = tenantId;
  if (state) {
    state = {
      ...state,
      tenantId,
      user: state.user ? { ...state.user, tenantId } : state.user,
    };
    persist();
  }
}

export function getCurrentUser(): UserProfile | null {
  return state?.user ?? null;
}

export function isAuthenticated(): boolean {
  return state !== null && state.expiresAt > Date.now();
}

export function applyTokenResponse(
  tokens: TokenResponse,
  opts: { mfaEnabled?: boolean } = {},
) {
  state = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: Date.now() + tokens.expiresInSeconds * 1000,
    tenantId: tokens.tenantId,
    user: {
      id: tokens.userId,
      email: tokens.email,
      displayName: tokens.email,
      active: true,
      mfaEnabled: Boolean(opts.mfaEnabled),
      mustChangePassword: tokens.mustChangePassword,
      tenantId: tokens.tenantId,
      roles: tokens.roles,
      permissions: tokens.permissions,
      createdAt: tokens.issuedAt,
      updatedAt: tokens.issuedAt,
    },
  };
  currentTenantId = tokens.tenantId;
  persist();
}

export async function login(
  email: string,
  password: string,
): Promise<UserProfile> {
  const res = await fetch(`${ENV.backendUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const json = (await res.json()) as ApiResponse<
    TokenResponse | MfaLoginChallenge
  >;
  if (!json.success || !json.data) {
    state = null;
    currentTenantId = null;
    persist();
    throw new AuthApiError(json.error ?? "Login failed", json.code, res.status);
  }
  if ((json.data as MfaLoginChallenge).mfaRequired === true) {
    throw new MfaRequiredError(json.data as MfaLoginChallenge);
  }
  applyTokenResponse(json.data as TokenResponse);
  return state!.user!;
}

export async function verifyMfaLogin(
  mfaChallengeToken: string,
  code: string,
  useRecoveryCode = false,
): Promise<UserProfile> {
  const res = await fetch(`${ENV.backendUrl}/auth/mfa/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      useRecoveryCode
        ? { mfaChallengeToken, recoveryCode: code }
        : { mfaChallengeToken, totpCode: code },
    ),
  });
  const json = (await res.json()) as ApiResponse<TokenResponse>;
  if (!json.success || !json.data) {
    state = null;
    currentTenantId = null;
    persist();
    throw new AuthApiError(
      json.error ?? "No se pudo verificar MFA",
      json.code,
      res.status,
    );
  }
  applyTokenResponse(json.data, { mfaEnabled: true });
  return state!.user!;
}

/** Login passwordless con passkey WebAuthn (Sprint 59). */
export async function loginWithPasskey(email: string): Promise<UserProfile> {
  const startRes = await fetch(`${ENV.backendUrl}/auth/webauthn/login/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const startJson = (await startRes.json()) as ApiResponse<{
    token: string;
    publicKey: RequestOptionsJSON;
  }>;
  if (!startJson.success || !startJson.data) {
    throw new AuthApiError(
      startJson.error ?? "No se pudo iniciar el acceso con passkey",
      startJson.code,
      startRes.status,
    );
  }
  const assertion = (await navigator.credentials.get({
    publicKey: requestOptionsFromJSON(startJson.data.publicKey),
  })) as PublicKeyCredential | null;
  if (!assertion) {
    throw new AuthApiError(
      "Autenticación con passkey cancelada",
      undefined,
      400,
    );
  }
  const response = assertion.response as AuthenticatorAssertionResponse;
  const finishRes = await fetch(
    `${ENV.backendUrl}/auth/webauthn/login/finish`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: startJson.data.token,
        id: assertion.id,
        authenticatorData: bufToB64url(response.authenticatorData),
        clientDataJSON: bufToB64url(response.clientDataJSON),
        signature: bufToB64url(response.signature),
        userHandle: response.userHandle
          ? bufToB64url(response.userHandle)
          : null,
      }),
    },
  );
  const finishJson = (await finishRes.json()) as ApiResponse<TokenResponse>;
  if (!finishJson.success || !finishJson.data) {
    state = null;
    currentTenantId = null;
    persist();
    throw new AuthApiError(
      finishJson.error ?? "No se pudo verificar la passkey",
      finishJson.code,
      finishRes.status,
    );
  }
  applyTokenResponse(finishJson.data);
  return state!.user!;
}

export interface LoginMethods {
  hasPasskey: boolean;
  sso: {
    enabled: boolean;
    provider: string | null;
    startUrl: string | null;
    buttonLabel: string | null;
  };
}

/** Paso 1 del login identifier-first (Sprint 60): métodos disponibles para un correo. */
export async function identifyLogin(email: string): Promise<LoginMethods> {
  const res = await fetch(`${ENV.backendUrl}/auth/login/identify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const json = (await res.json()) as ApiResponse<LoginMethods>;
  if (!json.success || !json.data) {
    throw new AuthApiError(
      json.error ?? "No se pudo identificar la cuenta",
      json.code,
      res.status,
    );
  }
  return json.data;
}

function decodeJwtClaims(token: string): Record<string, unknown> | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(part.length / 4) * 4, "=");
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

/**
 * Establece la sesión a partir de los tokens del callback SSO/AD (Sprint 60).
 * El perfil se hidrata decodificando los claims del access token.
 */
export function applySsoSession(
  accessToken: string,
  refreshToken: string,
  expiresInSeconds: number,
): UserProfile | null {
  const claims = decodeJwtClaims(accessToken);
  if (!claims) return null;
  const tenantId = typeof claims.tenantId === "string" ? claims.tenantId : null;
  state = {
    accessToken,
    refreshToken,
    expiresAt: Date.now() + expiresInSeconds * 1000,
    tenantId,
    user: {
      id: typeof claims.userId === "string" ? claims.userId : "",
      email: typeof claims.sub === "string" ? claims.sub : "",
      displayName: typeof claims.sub === "string" ? claims.sub : "",
      active: true,
      mfaEnabled: false,
      mustChangePassword: Boolean(claims.mustChangePassword),
      tenantId,
      roles: Array.isArray(claims.roles) ? (claims.roles as string[]) : [],
      permissions: Array.isArray(claims.permissions)
        ? (claims.permissions as string[])
        : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
  currentTenantId = tenantId;
  persist();
  return state.user;
}

export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch(`${ENV.backendUrl}/auth/password-reset/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok && res.status !== 204) {
    throw new AuthApiError(
      "No se pudo solicitar el restablecimiento",
      undefined,
      res.status,
    );
  }
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  const res = await fetch(`${ENV.backendUrl}/auth/password-reset/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword, confirmPassword }),
  });
  if (!res.ok && res.status !== 204) {
    const json = (await res
      .json()
      .catch(() => null)) as ApiResponse<unknown> | null;
    throw new AuthApiError(
      json?.error ?? "No se pudo restablecer la contraseña",
      json?.code,
      res.status,
    );
  }
}

export interface ActivationView {
  purpose: "ACTIVATION" | "AFFILIATION";
  email: string | null;
  displayName: string | null;
  tenantId: string | null;
  tenantName: string | null;
  valid: boolean;
  expired: boolean;
  consumed: boolean;
}

/** Resuelve un enlace de activación/afiliación (público, sin sesión). */
export async function resolveActivation(
  token: string,
): Promise<ActivationView> {
  const res = await fetch(
    `${ENV.backendUrl}/auth/activation?token=${encodeURIComponent(token)}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );
  const json = (await res
    .json()
    .catch(() => null)) as ApiResponse<ActivationView> | null;
  if (!res.ok || !json?.success || !json.data) {
    throw new AuthApiError(
      json?.error ?? "Enlace inválido o expirado",
      json?.code,
      res.status,
    );
  }
  return json.data;
}

/** Define la contraseña inicial vía token de activación (público, sin sesión). */
export async function confirmActivation(
  token: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  const res = await fetch(`${ENV.backendUrl}/auth/activation/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword, confirmPassword }),
  });
  if (!res.ok && res.status !== 204) {
    const json = (await res
      .json()
      .catch(() => null)) as ApiResponse<unknown> | null;
    throw new AuthApiError(
      json?.error ?? "No se pudo activar la cuenta",
      json?.code,
      res.status,
    );
  }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
): Promise<UserProfile> {
  const res = await authenticatedFetch(
    `${ENV.backendUrl}/auth/force-change-password`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    },
    { omitTenantHeader: true },
  );
  const json = (await res.json()) as ApiResponse<{
    mustChangePassword: boolean;
    passwordChangedAt?: string;
    passwordExpiresAt?: string | null;
  }>;
  if (!json.success || !json.data) {
    throw new Error(json.error ?? "No se pudo actualizar la contraseña");
  }
  if (!state?.user) throw new Error("No active session");
  state.user = {
    ...state.user,
    mustChangePassword: json.data.mustChangePassword,
    updatedAt: json.data.passwordChangedAt ?? state.user.updatedAt,
  };
  persist();
  return state.user;
}

async function doRefresh(): Promise<void> {
  if (!state) throw new Error("No active session");
  const res = await fetch(`${ENV.backendUrl}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: state.refreshToken }),
  });
  if (!res.ok) {
    state = null;
    persist();
    throw new Error("Refresh failed");
  }
  const json = (await res.json()) as ApiResponse<TokenResponse>;
  if (!json.success || !json.data) {
    state = null;
    persist();
    throw new Error(json.error ?? "Refresh failed");
  }
  applyTokenResponse(json.data);
}

function shouldRefresh(): boolean {
  if (!state) return false;
  return state.expiresAt - Date.now() < REFRESH_GRACE_MS;
}

function isSuperadminAdminRequest(input: RequestInfo | URL): boolean {
  if (!state?.user?.roles.includes("SUPERADMIN")) return false;
  const raw =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  try {
    const url = new URL(raw, window.location.origin);
    return url.pathname.startsWith("/api/v1/admin/");
  } catch {
    return raw.includes("/api/v1/admin/");
  }
}

export async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  opts: AuthenticatedFetchOptions = {},
): Promise<Response> {
  if (shouldRefresh()) {
    refreshPromise ??= doRefresh().finally(() => {
      refreshPromise = null;
    });
    try {
      await refreshPromise;
    } catch {
      window.location.assign("/login");
      throw new Error("Session expired");
    }
  }
  const headers = new Headers(init.headers);
  if (state?.accessToken)
    headers.set("Authorization", `Bearer ${state.accessToken}`);
  const tenantHeader =
    opts.tenantIdOverride ??
    (!opts.omitTenantHeader ? currentTenantId : null) ??
    (isSuperadminAdminRequest(input) ? "*" : null);
  if (tenantHeader) {
    headers.set("X-Tenant-Id", tenantHeader);
  }

  let response = await fetch(input, { ...init, headers });
  if (response.status === 401 && state) {
    try {
      await doRefresh();
      headers.set("Authorization", `Bearer ${state!.accessToken}`);
      response = await fetch(input, { ...init, headers });
    } catch {
      window.location.assign("/login");
    }
  }
  return response;
}

export async function logout(): Promise<void> {
  try {
    if (state) {
      await fetch(`${ENV.backendUrl}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${state.accessToken}`,
        },
        body: JSON.stringify({ refreshToken: state.refreshToken }),
      });
    }
  } catch {
    /* swallow */
  } finally {
    state = null;
    currentTenantId = null;
    persist();
  }
}

export function hasPermission(code: string): boolean {
  if (!state?.user) return false;
  if (state.user.roles.includes("SUPERADMIN")) return true;
  return state.user.permissions.includes(code);
}

export function hasAnyPermission(codes: string[]): boolean {
  return codes.some(hasPermission);
}

export function hasModuleLevel(moduleCode: string, level: RbacLevel): boolean {
  if (!state?.user) return false;
  if (state.user.roles.includes("SUPERADMIN")) return true;
  return hasModuleLevelFromPermissions(
    state.user.permissions,
    moduleCode,
    level,
  );
}
